import file_preview_component from "@/components/probaworkflows_base_components/FilePreviewComponent/FilePreviewComponent.vue";
import server_paginated_table_component from "@/components/probaworkflows_base_components/ServerPaginatedTable/ServerPaginatedTable.vue";
import {
  LIST_GENERATED_MANAGED_REPORTS_URL,
  get_headers,
  get_project_id,
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import axios from "axios";

export default {
  name: "GeneratedReports",
  components: {
    FilePreviewComponent: file_preview_component,
    ServerPaginatedTable: server_paginated_table_component,
  },
  mixins: [alert_mixin],
  data: () => ({
    headers: get_headers(),
    project_id: get_project_id(),
    loading_reports: false,
    searching_reports: false,
    reports: [],
    search_query: "",
    search_timer: null,
    last_applied_search: "",
    page_number: 1,
    next_page: null,
    prev_page: null,
    total_pages: 1,
    total_count: 0,
    page_size: 10,
    preview_report: null,
  }),
  computed: {
    summary_cards() {
      return [
        {
          label: "Report runs",
          value: this.total_count || 0,
          description: "Generated report records available for this project.",
        },
        {
          label: "Search",
          value: this.search_query.trim()
            ? "Filtered view"
            : "Showing all reports",
          description: "Results refresh automatically while you type.",
        },
        {
          label: "Preview",
          value: this.preview_report ? "Open" : "Ready",
          description: "Preview is available next to download when the file type supports it.",
        },
      ];
    },
    table_safe_reports() {
      return this.reports.map((report) => ({
        id: report.id,
        name: report.name,
        template: this.get_template_name(report),
        report_type: report.report_type?.display_name || "-",
        output_format: report.output_format?.display_name || "-",
        target: this.get_target_name(report),
        created: this.format_date(report.date_created),
        status: report.status?.display_name || "Completed",
      }));
    },
  },
  mounted() {
    this.load_reports();
  },
  beforeDestroy() {
    clearTimeout(this.search_timer);
  },
  methods: {
    build_report_params(page) {
      const params = {
        project_id: this.project_id,
        page_number: page,
        page_size: this.page_size,
        search: this.search_query.trim() || undefined,
      };
      return params;
    },
    apply_report_response(response) {
      this.reports = response.data.reports || [];
      this.total_count = response.data.total_count || 0;
      this.page_size = response.data.page_size || this.page_size;
      this.page_number = response.data.page_number || 1;
      this.next_page = response.data.next_page || null;
      this.prev_page = response.data.prev_page || null;
      this.total_pages = response.data.total_pages || 1;
    },
    handle_report_error(error) {
      this.reports = [];
      this.total_count = 0;
      this.show_error(error.response?.data?.message || "Failed to load generated reports");
    },
    run_report_request(page, loader = null) {
      return axios.get(LIST_GENERATED_MANAGED_REPORTS_URL, {
        headers: this.headers,
        params: this.build_report_params(page),
      })
        .then((response) => {
          this.apply_report_response(response);
        })
        .catch((error) => {
          this.handle_report_error(error);
        })
        .finally(() => {
          this.loading_reports = false;
          this.searching_reports = false;
          if (loader) {
            return this.close_loader(loader);
          }
          return null;
        });
    },
    load_reports(page = 1, use_loader = true) {
      this.loading_reports = true;
      if (!use_loader) {
        return this.run_report_request(page);
      }
      return this.show_loader("Loading generated reports...")
        .then((loader) => this.run_report_request(page, loader));
    },
    get_report_by_id(report_id) {
      return this.reports.find((report) => report.id === report_id) || null;
    },
    format_date(date_value) {
      if (!date_value) {
        return "Not available";
      }
      return new Date(date_value).toLocaleString();
    },
    go_to_page(page_number) {
      if (!page_number || page_number < 1 || page_number > this.total_pages) {
        return;
      }
      this.load_reports(page_number);
    },
    schedule_search() {
      clearTimeout(this.search_timer);
      this.search_timer = setTimeout(() => {
        this.apply_search();
      }, 250);
    },
    apply_search() {
      const normalized = this.search_query.trim();
      if (normalized === this.last_applied_search) {
        return;
      }
      this.searching_reports = true;
      this.last_applied_search = normalized;
      this.load_reports(1, false);
    },
    go_to_generate_reports() {
      this.$router.push("/generate_reports/");
    },
    go_to_report_templates() {
      this.$router.push("/report_templates/");
    },
    get_template_name(report) {
      return report.report_template?.name || "One-off report";
    },
    get_target_name(report) {
      return report.target_entity_profile?.name || "Project scope";
    },
    open_preview(row) {
      const report = this.get_report_by_id(row.id);
      if (!report?.presigned_url) {
        this.show_error("No preview is available for this report.");
        return;
      }
      this.preview_report = report;
    },
    get_preview_file_name(report) {
      if (!report) {
        return "";
      }
      const extension = report.output_format?.internal_name || "";
      return extension ? `${report.name}.${extension}` : report.name;
    },
    close_preview() {
      this.preview_report = null;
    },
  },
};
